// ---------------------------------------------------------------------------
// RBAC Catalog (Single source of truth)
// ---------------------------------------------------------------------------
// Mirrors the horizontal navbar (Sidebar.jsx -> navItems). RBAC-gated modules
// live here so Screen Access, runtime gates, and the backend stay aligned.
// Home (/admin/home) is omitted — it is available to every signed-in admin user
// and is not configurable per role.
//
// Shape stored per role:
//   screenAccess = {
//     <moduleKey>: {
//       view:    boolean,           // controls navbar tab visibility
//       create:  boolean,           // module-level action flags (optional)
//       edit:    boolean,           // kept for requirePermission() middleware
//       delete:  boolean,
//       approve: boolean,
//       export:  boolean,
//       screens: {
//         <screenKey>: { view, create, edit, delete, approve, export }
//       }
//     }
//   }
// ---------------------------------------------------------------------------

export const ACTIONS = ["view", "create", "edit", "delete", "approve", "export"];

/**
 * Stable UI order for the Screen Access wizard: one row per screen, actions
 * rendered left-to-right in this order (filtered by each screen’s `actions`).
 */
export const SCREEN_ACCESS_ACTION_ORDER = [
  "view",
  "create",
  "edit",
  "approve",
  "export",
  "delete",
];

/**
 * Coarse verb groupings (metadata / docs). The Screen Access wizard renders
 * actions in a single flat row per screen using `SCREEN_ACCESS_ACTION_ORDER`.
 */
export const SCREEN_ACCESS_ACTION_GROUPS = [
  {
    id: "access",
    title: "Access",
    description:
      "Allow opening this screen for this role. Turn off to hide it. Other options below only apply when this is on.",
    actions: ["view"],
  },
  {
    id: "create_import",
    title: "Create / import",
    description:
      "Adds new rows or runs an import/upload where this screen supports it.",
    actions: ["create"],
  },
  {
    id: "modify",
    title: "Modify",
    description:
      "Edit saved details or approve steps like leave or payroll—depends on this screen.",
    actions: ["edit", "approve"],
  },
  {
    id: "export",
    title: "Export",
    description: "Download spreadsheets or PDF-style outputs where available.",
    actions: ["export"],
  },
  {
    id: "deactivate",
    title: "Deactivate / remove",
    description:
      "Deactivate, delete, or close items—the checkbox label describes exactly what.",
    actions: ["delete"],
  },
];

/**
 * UX rule (stored shape unchanged):
 * - Turning **Edit** (or any mutator) **on** while View is off → View is turned on.
 * - Turning **View off** clears every mutator on that screen (Edit, etc. follow View off).
 * - `preferViewOff`: set when the user just unchecked View so we do not re‑enable View
 *   while Edit is still true in the same update payload.
 *
 * @param {object} [options]
 * @param {boolean} [options.preferViewOff]  User toggled View → false this update.
 */
export function normalizeScreenPermissionRow(
  screenPerms = {},
  declaredActions = [],
  options = {}
) {
  const declared = new Set(declaredActions);
  const row = { ...screenPerms };
  for (const action of ACTIONS) {
    if (!declared.has(action)) row[action] = false;
  }
  const mutators = SCREEN_ACCESS_ACTION_ORDER.filter(
    (a) => a !== "view" && declared.has(a)
  );

  if (options.preferViewOff && declared.has("view") && row.view === false) {
    mutators.forEach((a) => {
      row[a] = false;
    });
    return row;
  }

  const anyMutatorOn = mutators.some((a) => !!row[a]);
  if (anyMutatorOn) {
    row.view = true;
  }
  if (!row.view) {
    mutators.forEach((a) => {
      row[a] = false;
    });
  }
  return row;
}

/** Module-level flags = OR across screens (only where that screen declares the action). */
export function syncModuleFlagsFromScreens(moduleAcc, moduleDef) {
  if (!moduleDef?.screens?.length) return;
  const screens = moduleAcc.screens || {};
  for (const action of ACTIONS) {
    const anyScreen = moduleDef.screens.some(
      (s) =>
        Array.isArray(s.actions) &&
        s.actions.includes(action) &&
        !!(screens[s.key]?.[action])
    );
    moduleAcc[action] = anyScreen;
  }
}
export const OPERATION_CAPABILITIES = [
  "view",
  "create",
  "edit",
  "update",
  "delete",
  "approve",
  "import",
  "export",
];

const toTitleCase = (value = "") =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveCapabilities = (screen = {}) => {
  const actionSet = new Set(Array.isArray(screen.actions) ? screen.actions : []);
  const labelKey = `${screen.key || ""} ${screen.label || ""}`.toLowerCase();
  const importScreenKeys = new Set(["employee_list", "attendance_import"]);
  if (actionSet.has("edit")) actionSet.add("update");
  if (labelKey.includes("import") || importScreenKeys.has(screen.key)) {
    actionSet.add("import");
  }
  const prioritized = OPERATION_CAPABILITIES.filter((op) => actionSet.has(op));
  return prioritized;
};

// Ensures every screen has a labelled, title-cased entry for each declared
// action. Screens may ship a partial `actionLabels` map (preferred UX, e.g.
// "Save Changes" instead of "Edit"); missing keys fall back to Title Case.
const buildActionLabels = (screenDef = {}) => {
  const out = {};
  const supplied = screenDef.actionLabels || {};
  const actions = Array.isArray(screenDef.actions) ? screenDef.actions : [];
  for (const action of actions) {
    const value = supplied[action];
    out[action] = value && String(value).trim() ? String(value) : toTitleCase(action);
  }
  return out;
};

/** Short UI hints for Screen Access wizard only (e.g. button vs table row). Keys must match declared actions. */
const normalizeActionWhere = (raw, declaredActions = []) => {
  if (!raw || typeof raw !== "object") return undefined;
  const allowed = new Set(declaredActions);
  const out = {};
  for (const [key, val] of Object.entries(raw)) {
    if (!allowed.has(key)) continue;
    const s = String(val ?? "").trim();
    if (s) out[key] = s;
  }
  return Object.keys(out).length ? out : undefined;
};

const attachStrictMeta = (catalog) =>
  catalog.map((moduleDef) => {
    const screens = (moduleDef.screens || []).map((screenDef) => ({
      ...screenDef,
      screenName: screenDef.label || toTitleCase(screenDef.key),
      capabilities: deriveCapabilities(screenDef),
      actionLabels: buildActionLabels(screenDef),
      actionWhere: normalizeActionWhere(screenDef.actionWhere, screenDef.actions),
    }));
    const moduleCapabilities = OPERATION_CAPABILITIES.filter((op) =>
      screens.some((screenDef) => screenDef.capabilities.includes(op))
    );
    return {
      ...moduleDef,
      moduleName: moduleDef.label || toTitleCase(moduleDef.key),
      capabilities: moduleCapabilities,
      screens,
    };
  });

// The actions listed per screen. Every screen supports `view`; the rest are
// declared only when they make sense (e.g. Reports has `view` + `export`).

/** Employee self-service profile tabs (ESS + optional admin preview). Not in admin top-nav catalog. */
const PROFILE_MODULE_RAW = {
  key: "profile",
  label: "Profile",
  path: "/employee/profile",
  description:
    "Employee self-service profile tabs shown in the left employee panel profile page.",
  actions: ["view", "edit"],
  screens: [
    {
      key: "org_details",
      label: "Org Details",
      description: "Organization details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Org Details",
        edit: "Edit Org Details",
      },
    },
    {
      key: "personal_details",
      label: "Personal",
      description: "Personal details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Personal",
        edit: "Edit Personal",
      },
    },
    {
      key: "family_details",
      label: "Family",
      description: "Family details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Family",
        edit: "Edit Family",
      },
    },
    {
      key: "bank_details",
      label: "Bank",
      description: "Bank details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Bank",
        edit: "Edit Bank",
      },
    },
    {
      key: "education_details",
      label: "Education",
      description: "Education details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Education",
        edit: "Edit Education",
      },
    },
    {
      key: "profession_details",
      label: "Profession",
      description: "Profession details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Profession",
        edit: "Edit Profession",
      },
    },
    {
      key: "statutory_details",
      label: "Statutory Details",
      description: "Statutory details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Statutory Details",
        edit: "Edit Statutory Details",
      },
    },
    {
      key: "profile_documents",
      label: "Documents",
      description: "Documents tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Documents",
        edit: "Edit Documents",
      },
    },
  ],
};

/**
 * Historical / alternate `ess_quick_panel.screens` keys stored in DB or returned
 * by older `me/permissions` merges. Values are merged (OR per action) into the
 * canonical catalog key before `normalizeAccess` runs.
 *
 * - `regularization` → UI catalog uses `regularization_checks`.
 * - `review_requests` → closest ESS shortcut is pending approvals queue.
 */
export const ESS_QUICK_PANEL_LEGACY_SCREEN_ALIASES = {
  regularization: "regularization_checks",
  review_requests: "leave_request",
};

/**
 * Merges legacy quick-panel screen keys into canonical catalog keys. Leaves
 * other keys untouched (e.g. `announcements` when present in catalog).
 */
export function expandEssQuickPanelScreens(screens = {}) {
  if (!screens || typeof screens !== "object") return screens;
  const out = { ...screens };
  for (const [legacyKey, canonicalKey] of Object.entries(
    ESS_QUICK_PANEL_LEGACY_SCREEN_ALIASES
  )) {
    const leg = out[legacyKey];
    if (!leg || typeof leg !== "object") continue;
    const existing =
      out[canonicalKey] && typeof out[canonicalKey] === "object"
        ? out[canonicalKey]
        : {};
    const merged = { ...existing };
    for (const action of ACTIONS) {
      if (leg[action]) merged[action] = true;
    }
    out[canonicalKey] = merged;
  }
  return out;
}

/** ESS-only: right Quick Panel shortcuts (EmployeeSidebar.jsx rightItems). Granular request and approval types. */
const ESS_QUICK_PANEL_MODULE_RAW = {
  key: "ess_quick_panel",
  label: "Quick Panel",
  path: "/employee",
  description: "Right sidebar shortcuts for employee requests and approvals.",
  actions: ["view"],
  screens: [
    {
      key: "leave_request",
      label: "Leave Request",
      description: "Submit and view leave requests.",
      actions: ["view"],
      actionLabels: { view: "Show Leave Requests" },
    },
    {
      key: "change_shift",
      label: "Change Shift",
      description: "Request shift change.",
      actions: ["view"],
      actionLabels: { view: "Show Change Shift" },
    },
    {
      key: "resignation",
      label: "Resignation",
      description: "Submit resignation request.",
      actions: ["view"],
      actionLabels: { view: "Show Resignation" },
    },
    {
      key: "regularization_checks",
      label: "Regularization Checks",
      description: "Attendance regularization requests.",
      actions: ["view"],
      actionLabels: { view: "Show Regularization Checks" },
    },
    {
      key: "ticket_request",
      label: "Ticket",
      description: "Helpdesk ticket submission and tracking.",
      actions: ["view"],
      actionLabels: { view: "Show Tickets" },
    },
    {
      key: "tax_declaration",
      label: "Tax Declaration",
      description: "Tax declaration forms and submissions.",
      actions: ["view"],
      actionLabels: { view: "Show Tax Declarations" },
    },
    {
      key: "mrf_request",
      label: "MRF",
      description: "Material Request Form submissions.",
      actions: ["view"],
      actionLabels: { view: "Show MRF" },
    },
    {
      key: "short_leave",
      label: "Short Leave",
      description: "Short leave or casual absence requests.",
      actions: ["view"],
      actionLabels: { view: "Show Short Leave" },
    },
    {
      key: "announcements",
      label: "Announcements",
      description:
        "Company announcements shortcut (parity with stored screen_access keys; wire in ESS UI when needed).",
      actions: ["view"],
      actionLabels: { view: "Show Announcements" },
    },
  ],
};

const HOME_MODULE_RAW = {
  key: "home",
  label: "Home",
  path: "/home",
  description: "Home dashboard — employee landing page and admin celebration/glance widgets.",
  actions: ["view"],
  screens: [
    {
      key: "dashboard",
      label: "Dashboard",
      description: "Main home page content and employee landing screen.",
      actions: ["view"],
      actionLabels: { view: "View Home" },
      actionWhere: { view: "(home page)" },
    },
    {
      key: "birthdays",
      label: "Birthdays",
      description: "Upcoming employee birthdays widget on the home dashboard.",
      actions: ["view"],
      actionLabels: { view: "Show Birthdays Widget" },
      actionWhere: { view: "(home dashboard - Birthdays card)" },
    },
    {
      key: "work_anniversaries",
      label: "Work Anniversaries",
      description: "Upcoming work anniversaries widget on the home dashboard.",
      actions: ["view"],
      actionLabels: { view: "Show Work Anniversaries Widget" },
      actionWhere: { view: "(home dashboard - Work Anniversaries card)" },
    },
    {
      key: "new_joinees",
      label: "New Joinees",
      description: "Upcoming and recent new joinee widget on the home dashboard.",
      actions: ["view"],
      actionLabels: { view: "Show New Joinees Widget" },
      actionWhere: { view: "(home dashboard - New Joinees card)" },
    },
    {
      key: "glance",
      label: "Today at a Glance",
      description: "Daily headcount, on-leave count, and pending approvals summary widget.",
      actions: ["view"],
      actionLabels: { view: "Show Today at a Glance Widget" },
      actionWhere: { view: "(home dashboard - Today at a Glance section)" },
    },
  ],
};

const ESS_ATTENDANCE_MODULE_RAW = {
  key: "ess_attendance",
  label: "ESS Attendance",
  path: "/employee/attendance",
  description: "Employee self-service attendance.",
  actions: ["view", "edit"],
  screens: [
    {
      key: "self_service",
      label: "Self Service",
      description: "Employee attendance calendar and requests.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Attendance",
        edit: "Submit / edit own attendance actions",
      },
    },
  ],
};

const ESS_PAYROLL_MODULE_RAW = {
  key: "ess_payroll",
  label: "ESS Payroll",
  path: "/employee/payroll",
  description: "Employee self-service payslips and payroll views.",
  actions: ["view", "edit"],
  screens: [
    {
      key: "self_service",
      label: "Self Service",
      description: "Employee payroll and payslip views.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Payroll",
        edit: "Edit declarations / inputs (if applicable)",
      },
    },
  ],
};

const ESS_REPORTS_MODULE_RAW = {
  key: "ess_reports",
  label: "ESS Reports",
  path: "/employee/reports",
  description: "Employee-facing reports.",
  actions: ["view", "export"],
  screens: [
    {
      key: "reports",
      label: "Reports",
      description: "Employee reports listing and exports.",
      actions: ["view", "export"],
      actionLabels: {
        view: "View Reports",
        export: "Export",
      },
    },
  ],
};

const MY_TEAM_MODULE_RAW = {
  key: "my_team",
  label: "My Team",
  path: "/employee/my-team",
  description: "Direct reportees list and manager self-service.",
  actions: ["view"],
  screens: [
    {
      key: "reportee_list",
      label: "Team list",
      description: "View and interact with direct reportees.",
      actions: ["view"],
      actionLabels: { view: "View My Team" },
    },
  ],
};

const ADMIN_CATALOG_SOURCE = [
  {
    key: "configuration",
    label: "Configuration",
    path: "/admin/configuration",
    description:
      "Three sections as seen in the Configuration sidebar: Attendance Codes, Enterprise, Masters.",
    actions: ["view", "create", "edit", "delete"],
    screens: [
      {
        key: "enterprise",
        label: "Enterprise",
        description:
          "View and save the company/organization profile (Enterprise Details).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Company Profile",
          edit: "Save Company Details",
        },
        actionWhere: {
          view: "(form page)",
          edit: "(Save button)",
        },
      },
      {
        key: "masters",
        label: "Masters",
        description:
          "Create, edit and deactivate Branch, Department, Designation, Sub-Department, Grade, Category and Reasons.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Masters List",
          create: "Create New Master",
          edit: "Edit Master Record",
          delete: "Deactivate Master",
        },
        actionWhere: {
          view: "(tables)",
          create: "(toolbar - New button)",
          edit: "(table row - Edit)",
          delete: "(table row - Deactivate)",
        },
      },
      {
        key: "attendance_codes",
        label: "Attendance Codes",
        description:
          "Configure attendance marking codes (Present, Absent, Leave, etc.)",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Attendance Codes",
          edit: "Configure Codes",
        },
        actionWhere: {
          view: "(codes table)",
          edit: "(inline edit or Save)",
        },
      },
    ],
  },
  {
    key: "employee",
    label: "Employee",
    path: "/admin/employee",
    description: "Employee directory and lifecycle (onboarding, exit).",
    actions: ["view", "create", "edit", "approve", "export"],
    screens: [
      {
        key: "employee_list",
        label: "Employee List",
        description: "Lists employees with search, filter, and bulk actions.",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Employee Table",
          create: "Import / Add New Employee",
          edit: "Edit Employee Record",
          export: "Export Employee List",
        },
        actionWhere: {
          view: "(table)",
          create: "(toolbar - Import/New Employee button)",
          edit: "(row menu - View/Edit/Confirmation/Transfer/Salary Revision/Resignation)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "create_employee",
        label: "Create Employee",
        description: "Creates new employee profiles and onboarding records.",
        actions: ["view", "create"],
        actionLabels: {
          view: "View Create Form",
          create: "Create New Employee",
        },
        actionWhere: {
          view: "(form page)",
          create: "(Save button in form)",
        },
      },
      {
        key: "edit_employee",
        label: "Edit Employee",
        description: "Updates existing employee profile and employment details.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Employee Details",
          edit: "Save Employee Changes",
        },
        actionWhere: {
          view: "(detail panel)",
          edit: "(Save button in form)",
        },
      },
      {
        key: "employee_profile",
        label: "Employee Profile",
        description: "Shows full employee profile and personal information.",
        actions: ["view"],
        actionLabels: {
          view: "View Full Profile",
        },
        actionWhere: {
          view: "(profile panel)",
        },
      },
      {
        key: "employee_documents",
        label: "Documents",
        description: "Manages employee document uploads and exports.",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Documents",
          create: "Upload Document",
          edit: "Edit Document",
          export: "Download / Export Documents",
        },
        actionWhere: {
          view: "(documents tab)",
          create: "(toolbar - Upload button)",
          edit: "(row menu)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "resignation",
        label: "Resignation / Exit",
        description: "Handles employee resignation and exit workflow.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Resignation Requests",
          create: "Initiate Resignation",
          edit: "Update Resignation Details",
          approve: "Approve Resignation",
        },
        actionWhere: {
          view: "(resignation popup)",
          create: "(Initiate button)",
          edit: "(form - Save button)",
          approve: "(Approve button)",
        },
      },
      {
        key: "transfer",
        label: "Transfer",
        description: "Processes employee transfer requests and approvals.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Transfer Requests",
          create: "Initiate Transfer",
          edit: "Update Transfer Details",
          approve: "Approve Transfer",
        },
        actionWhere: {
          view: "(transfer popup)",
          create: "(Initiate button)",
          edit: "(form - Save button)",
          approve: "(Approve button)",
        },
      },
      {
        key: "confirmation",
        label: "Confirmation",
        description: "Handles employee confirmation and probation decisions.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Confirmation Records",
          create: "Initiate Confirmation",
          edit: "Update Confirmation Details",
          approve: "Approve Confirmation",
        },
        actionWhere: {
          view: "(confirmation popup)",
          create: "(Initiate button)",
          edit: "(form - Save button)",
          approve: "(Approve button)",
        },
      },
      {
        key: "employee_reports",
        label: "Employee Reports",
        description: "Standard employee reports — Employee, Age, Service, Salary, Confirmation, Transfers, Salary Revision, Resignations.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Employee Reports",
          export: "Download / Export Reports",
        },
        actionWhere: {
          view: "(Reports section in left nav)",
          export: "(Download button per report)",
        },
      },
      {
        key: "employee_export",
        label: "Employee Data Export",
        description: "Export employee data by category — Org Details, Personal, Family, Bank, Education, Professional, Statutory, All Data.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Export Section",
          export: "Export Employee Data",
        },
        actionWhere: {
          view: "(Export section in left nav)",
          export: "(Download button per export type)",
        },
      },
    ],
  },
  {
    key: "attendance",
    label: "Attendance",
    path: "/admin/attendance",
    description: "Daily attendance, regularization and leave requests.",
    actions: ["view", "create", "edit", "approve", "export"],
    screens: [
      {
        key: "attendance_dashboard",
        label: "Attendance Dashboard",
        description: "Shows attendance KPIs, trends, and monthly summaries.",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Attendance Summary",
          create: "Mark All Attendance",
          edit: "Lock/Unlock Attendance",
          export: "Export Attendance Report",
        },
        actionWhere: {
          view: "(dashboard)",
          create: "(Mark All button)",
          edit: "(Lock/Unlock button)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "mark_attendance",
        label: "Mark Attendance",
        description: "Marks and edits day-wise employee attendance records.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Attendance Grid",
          create: "Mark Attendance",
          edit: "Edit Attendance Record",
        },
        actionWhere: {
          view: "(attendance table)",
          create: "(inline cell edit / Mark All modal)",
          edit: "(cell or Edit menu)",
        },
      },
      {
        key: "attendance_history",
        label: "Attendance History",
        description: "Displays historical attendance logs and exportable data.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Attendance History",
          export: "Export History Report",
        },
        actionWhere: {
          view: "(history page)",
          export: "(Export button)",
        },
      },
      {
        key: "attendance_import",
        label: "Bulk Import",
        description: "Imports attendance data from template or file upload.",
        actions: ["view", "create"],
        actionLabels: {
          view: "View Import Interface",
          create: "Upload & Import Attendance",
        },
        actionWhere: {
          view: "(import page)",
          create: "(Upload File button)",
        },
      },
      {
        key: "regularization",
        label: "Regularization Requests",
        description: "Reviews and approves attendance regularization requests.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Regularization Requests",
          create: "Create Regularization Request",
          edit: "Update Regularization Details",
          approve: "Approve Regularization",
        },
        actionWhere: {
          view: "(request list)",
          create: "(Create button)",
          edit: "(row menu - Edit)",
          approve: "(Approve button)",
        },
      },
      {
        key: "leave_requests",
        label: "Leave Requests",
        description: "Manages leave requests with approval workflow.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Leave Requests",
          create: "Create Leave Request",
          edit: "Update Leave Request",
          approve: "Approve Leave Request",
        },
        actionWhere: {
          view: "(request list)",
          create: "(Create button)",
          edit: "(row menu - Edit)",
          approve: "(Approve button)",
        },
      },
    ],
  },
  {
    key: "payroll",
    label: "Payroll",
    path: "/admin/payroll",
    description: "Salary structures, processing and payslips.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
    screens: [
      {
        key: "payroll_dashboard",
        label: "Payroll Dashboard",
        description: "Shows payroll KPIs, processing status, and summaries.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Payroll Dashboard",
          export: "Export Payroll Report",
        },
        actionWhere: {
          view: "(dashboard)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "salary_structure",
        label: "Salary Structure",
        description: "Defines salary components and salary structure templates.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Salary Structures",
          create: "Create New Structure",
          edit: "Edit Structure",
          delete: "Delete Structure",
        },
        actionWhere: {
          view: "(structure list)",
          create: "(New Structure button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "statutory_rates",
        label: "Statutory Rates",
        description: "Maintains statutory deduction and compliance rates.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Statutory Rates",
          edit: "Update Rates",
        },
        actionWhere: {
          view: "(rates table)",
          edit: "(inline or row edit)",
        },
      },
      {
        key: "salary_simulator",
        label: "Salary Simulator",
        description: "Simulates payroll output for salary scenarios.",
        actions: ["view"],
        actionLabels: {
          view: "View Salary Simulator",
        },
        actionWhere: {
          view: "(simulator page)",
        },
      },
      {
        key: "formula_manager",
        label: "Formula Manager",
        description: "Creates and edits payroll calculation formulas.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Formulas",
          create: "Create Formula",
          edit: "Edit Formula",
          delete: "Delete Formula",
        },
        actionWhere: {
          view: "(formula list)",
          create: "(New Formula button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "process_payroll",
        label: "Process Payroll",
        description: "Runs payroll processing and approval for a pay cycle.",
        actions: ["view", "create", "approve"],
        actionLabels: {
          view: "View Payroll Processing",
          create: "Process Payroll",
          approve: "Approve Payroll",
        },
        actionWhere: {
          view: "(processing page)",
          create: "(Process Payroll button)",
          approve: "(Approve button)",
        },
      },
      {
        key: "payslips",
        label: "Payslips",
        description: "Generates and exports employee payslips.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Payslips",
          export: "Download Payslip",
        },
        actionWhere: {
          view: "(payslip list)",
          export: "(Download button)",
        },
      },
      {
        key: "tax_declaration",
        label: "Tax Declaration",
        description: "Manages employee tax declarations and approvals.",
        actions: ["view", "create", "edit", "approve", "export"],
        actionLabels: {
          view: "View Tax Declarations",
          create: "Upload Declaration",
          edit: "Update Declaration",
          approve: "Approve Declaration",
          export: "Export Declaration",
        },
        actionWhere: {
          view: "(declaration list)",
          create: "(Upload button)",
          edit: "(row menu - Edit)",
          approve: "(Approve button)",
          export: "(Export button)",
        },
      },
    ],
  },
  {
    key: "hiring",
    label: "Hiring",
    path: "/admin/hiring",
    description: "Open positions, candidates and offers.",
    actions: ["view", "create", "edit", "delete", "approve"],
    screens: [
      {
        key: "positions",
        label: "Positions",
        description: "Creates and manages open job positions.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Open Positions",
          create: "Create New Vacancy",
          edit: "Edit Vacancy Details",
          delete: "Close Vacancy",
        },
        actionWhere: {
          view: "(positions page)",
          create: "(New Vacancy button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "candidates",
        label: "Candidates",
        description: "Tracks candidate pipeline and profile updates.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Candidates",
          create: "Import / Add Candidate",
          edit: "Update Candidate Profile",
          delete: "Remove Candidate",
        },
        actionWhere: {
          view: "(candidates page)",
          create: "(toolbar - Import button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "offers",
        label: "Offers",
        description: "Prepares, edits, and approves offer letters.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Offers",
          create: "Create Offer Letter",
          edit: "Edit Offer",
          approve: "Approve Offer",
        },
        actionWhere: {
          view: "(offers page)",
          create: "(New Offer button)",
          edit: "(row menu)",
          approve: "(Approve button)",
        },
      },
    ],
  },
  {
    key: "assets",
    label: "Assets",
    path: "/admin/assets",
    description: "Company asset inventory and assignments.",
    actions: ["view", "create", "edit", "delete", "export"],
    screens: [
      {
        key: "asset_list",
        label: "Asset List",
        description: "Maintains asset inventory and lifecycle records.",
        actions: ["view", "create", "edit", "delete", "export"],
        actionLabels: {
          view: "View All Assets",
          create: "Add New Asset",
          edit: "Edit Asset Details",
          delete: "Delete Asset",
          export: "Export Asset List",
        },
        actionWhere: {
          view: "(assets page)",
          create: "(Add Asset button)",
          edit: "(row menu)",
          delete: "(row menu)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "assign_asset",
        label: "Assign Asset",
        description: "Assigns and updates assets mapped to employees.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Asset Assignments",
          create: "Assign Asset to Employee",
          edit: "Update Assignment",
        },
        actionWhere: {
          view: "(assignments page)",
          create: "(Assign button)",
          edit: "(row menu)",
        },
      },
      {
        key: "asset_reports",
        label: "Asset Reports",
        description: "Shows downloadable reports for asset movements.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Asset Reports",
          export: "Download Report",
        },
        actionWhere: {
          view: "(reports page)",
          export: "(Export button)",
        },
      },
    ],
  },
  {
    key: "communication",
    label: "Communication",
    path: "/admin/communication",
    description: "Flash news, announcements, polls.",
    actions: ["view", "create", "edit", "delete"],
    screens: [
      {
        key: "flash_news",
        label: "Flash News",
        description: "Publishes short internal flash updates to users.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Flash News",
          create: "Create Flash News",
          edit: "Edit Flash News",
          delete: "Delete Flash News",
        },
        actionWhere: {
          view: "(flash news page)",
          create: "(New News button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "announcements",
        label: "Announcements",
        description: "Publishes organization-wide formal announcements.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Announcements",
          create: "Create Announcement",
          edit: "Edit Announcement",
          delete: "Delete Announcement",
        },
        actionWhere: {
          view: "(announcements page)",
          create: "(New Announcement button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "polls",
        label: "Polls",
        description: "Creates and manages employee polls and votes.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Polls",
          create: "Create Poll",
          edit: "Edit / Publish Poll",
          delete: "Delete Poll",
        },
        actionWhere: {
          view: "(polls page)",
          create: "(New Poll button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
    ],
  },
  {
    key: "helpdesk",
    label: "Helpdesk",
    path: "/admin/helpdesk",
    description: "Ticket inbox and helpdesk configuration.",
    actions: ["view", "create", "edit", "delete", "approve"],
    screens: [
      {
        key: "tickets",
        label: "Tickets",
        description: "Manages support tickets and resolution workflow.",
        actions: ["view", "create", "edit", "delete", "approve"],
        actionLabels: {
          view: "View Tickets",
          create: "Create Ticket",
          edit: "Update Ticket Status",
          delete: "Delete Ticket",
          approve: "Approve Resolution",
        },
        actionWhere: {
          view: "(tickets list)",
          create: "(New Ticket button)",
          edit: "(ticket detail - Save)",
          delete: "(row menu)",
          approve: "(Approve button)",
        },
      },
      {
        key: "helpdesk_config",
        label: "Helpdesk Config",
        description: "Configures helpdesk categories, rules, and defaults.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Configuration",
          edit: "Edit Configuration",
        },
        actionWhere: {
          view: "(config page)",
          edit: "(Save button)",
        },
      },
    ],
  },
  {
    key: "user_roles",
    label: "User Roles",
    path: "/admin/user-role-management",
    description: "Role creation + screen/data access configuration.",
    actions: ["view", "create", "edit", "delete"],
    screens: [
      {
        key: "role_list",
        label: "Role List",
        description: "Creates, edits, and manages available custom roles.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Roles",
          create: "Create Custom Role",
          edit: "Edit Role Details",
          delete: "Delete Role",
        },
        actionWhere: {
          view: "(roles list)",
          create: "(New Role button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "screen_access",
        label: "Screen Access",
        description: "Controls module/screen-level action permissions.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Screen Permissions",
          edit: "Update Screen Permissions",
        },
        actionWhere: {
          view: "(permissions grid)",
          edit: "(Save button)",
        },
      },
      {
        key: "data_access",
        label: "Data Access",
        description: "Configures data visibility scope for each role.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Data Scope",
          edit: "Update Data Scope",
        },
        actionWhere: {
          view: "(scope grid)",
          edit: "(Save button)",
        },
      },
    ],
  },
];

const ESS_CATALOG_SOURCE = [
  ESS_QUICK_PANEL_MODULE_RAW,
  PROFILE_MODULE_RAW,
  HOME_MODULE_RAW,
  ESS_ATTENDANCE_MODULE_RAW,
  ESS_PAYROLL_MODULE_RAW,
  ESS_REPORTS_MODULE_RAW,
  MY_TEAM_MODULE_RAW,
];

export const ADMIN_RBAC_CATALOG = attachStrictMeta(ADMIN_CATALOG_SOURCE);
export const ESS_SYSTEM_RBAC_CATALOG = attachStrictMeta(ESS_CATALOG_SOURCE);

/** Admin (custom-role) catalog: top-nav modules only. Alias kept for backward compatibility. */
export const CATALOG = ADMIN_RBAC_CATALOG;

/**
 * Screen Access wizard + stored JSON shape for roles: system → ESS shell catalog;
 * custom → admin app catalog.
 */
export function getScreenCatalogForRole(role) {
  if (role && String(role.type || "").toLowerCase() === "system") {
    return ESS_SYSTEM_RBAC_CATALOG;
  }
  return ADMIN_RBAC_CATALOG;
}

// Must mirror Sidebar.jsx nav tab order. Includes `home`; Home is not in CATALOG.
export const TOP_NAV_MODULE_KEYS = [
  "home",
  "configuration",
  "employee",
  "attendance",
  "payroll",
  "hiring",
  "assets",
  "communication",
  "helpdesk",
  "user_roles",
];

const uniq = (arr) => [...new Set(arr)];

export const validateCatalogConsistency = ({
  catalog = ADMIN_RBAC_CATALOG,
  actionKeys = ACTIONS,
  topNavModuleKeys = TOP_NAV_MODULE_KEYS,
  requireTopNavAlignment = true,
} = {}) => {
  const issues = [];
  const moduleKeys = catalog.map((m) => m.key);
  const duplicateModules = moduleKeys.filter(
    (key, idx) => moduleKeys.indexOf(key) !== idx
  );
  if (duplicateModules.length) {
    issues.push(`Duplicate module keys: ${uniq(duplicateModules).join(", ")}`);
  }

  for (const mod of catalog) {
    const moduleActions = Array.isArray(mod.actions) ? mod.actions : [];
    const invalidModuleActions = moduleActions.filter(
      (a) => !actionKeys.includes(a)
    );
    if (invalidModuleActions.length) {
      issues.push(
        `Module "${mod.key}" has invalid actions: ${invalidModuleActions.join(", ")}`
      );
    }

    if (!Array.isArray(mod.screens) || mod.screens.length === 0) {
      issues.push(`Module "${mod.key}" has no screens configured.`);
      continue;
    }

    const screenKeys = mod.screens.map((s) => s.key);
    const duplicateScreenKeys = screenKeys.filter(
      (key, idx) => screenKeys.indexOf(key) !== idx
    );
    if (duplicateScreenKeys.length) {
      issues.push(
        `Module "${mod.key}" has duplicate screen keys: ${uniq(
          duplicateScreenKeys
        ).join(", ")}`
      );
    }

    for (const screen of mod.screens) {
      const screenActions = Array.isArray(screen.actions) ? screen.actions : [];
      if (!screenActions.includes("view")) {
        issues.push(`Screen "${mod.key}.${screen.key}" is missing required "view" action.`);
      }
      const invalidScreenActions = screenActions.filter(
        (a) => !actionKeys.includes(a)
      );
      if (invalidScreenActions.length) {
        issues.push(
          `Screen "${mod.key}.${screen.key}" has invalid actions: ${invalidScreenActions.join(", ")}`
        );
      }
      const outOfModuleActions = screenActions.filter(
        (a) => !moduleActions.includes(a)
      );
      if (outOfModuleActions.length) {
        issues.push(
          `Screen "${mod.key}.${screen.key}" uses actions not declared at module level: ${outOfModuleActions.join(", ")}`
        );
      }
      if (!screen.description || !String(screen.description).trim()) {
        issues.push(`Screen "${mod.key}.${screen.key}" is missing description.`);
      }
      if (screen.actionLabels && typeof screen.actionLabels === "object") {
        const strayLabelKeys = Object.keys(screen.actionLabels).filter(
          (k) => !screenActions.includes(k)
        );
        if (strayLabelKeys.length) {
          issues.push(
            `Screen "${mod.key}.${screen.key}" has actionLabels for undeclared actions: ${strayLabelKeys.join(", ")}`
          );
        }
      }
      if (screen.actionWhere && typeof screen.actionWhere === "object") {
        const strayWhereKeys = Object.keys(screen.actionWhere).filter(
          (k) => !screenActions.includes(k)
        );
        if (strayWhereKeys.length) {
          issues.push(
            `Screen "${mod.key}.${screen.key}" has actionWhere for undeclared actions: ${strayWhereKeys.join(", ")}`
          );
        }
      }
    }
  }

  if (requireTopNavAlignment) {
    const missingInCatalog = topNavModuleKeys
      .filter((k) => k !== "home")
      .filter((k) => !moduleKeys.includes(k));
    if (missingInCatalog.length) {
      issues.push(
        `Top-nav modules missing in catalog: ${missingInCatalog.join(", ")}`
      );
    }
  }

  return issues;
};

const adminCatalogIssues = validateCatalogConsistency({
  catalog: ADMIN_RBAC_CATALOG,
  requireTopNavAlignment: true,
});
const essCatalogIssues = validateCatalogConsistency({
  catalog: ESS_SYSTEM_RBAC_CATALOG,
  requireTopNavAlignment: false,
});

export const CATALOG_VALIDATION_ISSUES = [...adminCatalogIssues, ...essCatalogIssues];

if (typeof window !== "undefined" && CATALOG_VALIDATION_ISSUES.length > 0) {
  console.warn("[RBAC] Catalog validation issues:", CATALOG_VALIDATION_ISSUES);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const MODULE_KEYS = ADMIN_RBAC_CATALOG.map((m) => m.key);

export const ESS_MODULE_KEYS = ESS_SYSTEM_RBAC_CATALOG.map((m) => m.key);

export const getModule = (moduleKey, catalog = ADMIN_RBAC_CATALOG) =>
  catalog.find((m) => m.key === moduleKey) || null;

export const getScreen = (moduleKey, screenKey, catalog = ADMIN_RBAC_CATALOG) => {
  const mod = getModule(moduleKey, catalog);
  if (!mod) return null;
  return mod.screens.find((s) => s.key === screenKey) || null;
};

// UI-friendly label for an action on a specific screen. Falls back to Title
// Case of the action key if the screen did not declare a custom label.
export const getActionLabel = (moduleKey, screenKey, action, catalog = ADMIN_RBAC_CATALOG) => {
  if (!action) return "";
  const screen = getScreen(moduleKey, screenKey, catalog);
  const label = screen?.actionLabels?.[action];
  return label && String(label).trim() ? label : toTitleCase(action);
};

// Returns a fresh "all false" permission shape matching the catalog. Useful
// when seeding a blank role or filling in missing keys at runtime.
export const buildEmptyAccess = (catalog = ADMIN_RBAC_CATALOG) => {
  const out = {};
  for (const mod of catalog) {
    const moduleAcc = {};
    for (const action of ACTIONS) moduleAcc[action] = false;
    const screens = {};
    for (const screen of mod.screens) {
      const screenAcc = {};
      for (const action of ACTIONS) screenAcc[action] = false;
      screens[screen.key] = screenAcc;
    }
    moduleAcc.screens = screens;
    out[mod.key] = moduleAcc;
  }
  return out;
};

/** All permissions true for every declared action (used when seeding default ESS roles). */
export const buildFullAccess = (catalog = ESS_SYSTEM_RBAC_CATALOG) => {
  const out = {};
  for (const mod of catalog) {
    const moduleAcc = { screens: {} };
    for (const action of ACTIONS) moduleAcc[action] = false;
    for (const screen of mod.screens) {
      const screenAcc = {};
      for (const action of ACTIONS) screenAcc[action] = false;
      for (const a of screen.actions || []) {
        if (ACTIONS.includes(a)) screenAcc[a] = true;
      }
      moduleAcc.screens[screen.key] = normalizeScreenPermissionRow(
        screenAcc,
        screen.actions
      );
    }
    syncModuleFlagsFromScreens(moduleAcc, mod);
    out[mod.key] = moduleAcc;
  }
  return out;
};

/**
 * True when stored access predates the current ESS module set (e.g. missing
 * `home`). Left-nav visibility is derived from profile / attendance / payroll /
 * reports / my_team — not a separate `ess_left_nav` module.
 */
export const isLegacyEssScreenAccess = (raw) => {
  if (!raw || typeof raw !== "object") return true;
  const home = raw.home ?? raw.ess_home;
  return !home || typeof home !== "object";
};

/**
 * Default ESS screenAccess for seeded system roles. EMPLOYEE: full ESS except My Team;
 * MANAGER: full ESS including My Team.
 */
export const buildDefaultEssSystemRoleAccess = (roleCode) => {
  const full = buildFullAccess(ESS_SYSTEM_RBAC_CATALOG);
  const code = String(roleCode || "").toUpperCase();
  if (code !== "EMPLOYEE") return full;
  const teamMod = full.my_team;
  if (teamMod?.screens?.reportee_list) {
    for (const a of ACTIONS) teamMod.screens.reportee_list[a] = false;
    const teamDef = ESS_SYSTEM_RBAC_CATALOG.find((m) => m.key === "my_team");
    if (teamDef) syncModuleFlagsFromScreens(teamMod, teamDef);
  }
  return full;
};

// Merges an arbitrary saved shape onto the canonical shape so older/partial
// rows still work (defensive read). Never mutates input.
export const normalizeAccess = (raw, catalog = ADMIN_RBAC_CATALOG) => {
  const base = buildEmptyAccess(catalog);
  if (!raw || typeof raw !== "object") return base;

  for (const mod of catalog) {
    const rawMod = raw[mod.key];
    if (!rawMod || typeof rawMod !== "object") continue;

    for (const action of ACTIONS) {
      if (action !== "view") continue;
      if (rawMod[action] === true) base[mod.key][action] = true;
    }

    const rawScreens =
      rawMod.screens && typeof rawMod.screens === "object"
        ? rawMod.screens
        : null;

    if (rawScreens) {
      for (const screen of mod.screens) {
        const rawScr = rawScreens[screen.key];
        if (!rawScr || typeof rawScr !== "object") continue;
        for (const action of ACTIONS) {
          if (rawScr[action] === true) {
            base[mod.key].screens[screen.key][action] = true;
          }
        }
      }
    }

    for (const screen of mod.screens) {
      const rawScrForScreen =
        rawScreens && typeof rawScreens === "object" ? rawScreens[screen.key] : null;
      const preferViewOffIngest =
        rawScrForScreen &&
        typeof rawScrForScreen === "object" &&
        rawScrForScreen.view === false;
      base[mod.key].screens[screen.key] = normalizeScreenPermissionRow(
        base[mod.key].screens[screen.key],
        screen.actions,
        preferViewOffIngest ? { preferViewOff: true } : {}
      );
    }

    syncModuleFlagsFromScreens(base[mod.key], mod);

    for (const action of ACTIONS) {
      if (action !== "view") continue;
      if (rawMod[action] === true) base[mod.key][action] = true;
    }
  }
  return base;
};
